import React from 'react';
import { Box, Typography } from '@mui/material';

const STRAPI_URL = (process.env.REACT_APP_STRAPI_URL || 'https://cms.symuliga.pl').replace(/\/$/, '');

const isSafeUrl = (url) => {
  if (!url) return false;
  return url.startsWith('/') || url.startsWith('#') || /^https?:\/\//i.test(url) || /^mailto:/i.test(url);
};

const resolveMediaUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
};

const ArticleLink = ({ href, children }) => (
  <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" style={{ fontWeight: 700 }}>
    {children}
  </a>
);

const renderInlineMarkdown = (text) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={parts.length}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      parts.push(<em key={parts.length}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={parts.length}>{token.slice(1, -1)}</code>);
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const label = linkMatch?.[1] || token;
      const url = isSafeUrl(linkMatch?.[2]) ? linkMatch[2] : isSafeUrl(label) ? label : linkMatch?.[2];

      parts.push(
        isSafeUrl(url) ? (
          <ArticleLink key={parts.length} href={url}>
            {label}
          </ArticleLink>
        ) : (
          label
        )
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const renderMarkdownBody = (body, keyPrefix) => {
  const lines = body.split(/\r?\n/);
  const blocks = [];
  let listItems = [];
  let listComponent = 'ul';

  const flushList = () => {
    if (listItems.length === 0) return;
    const Component = listComponent;
    blocks.push(
      <Box key={`${keyPrefix}-list-${blocks.length}`} component={Component} sx={{ my: 2, pl: 3, lineHeight: 1.8 }}>
        {listItems.map((item, index) => (
          <li key={index}>{renderInlineMarkdown(item)}</li>
        ))}
      </Box>
    );
    listItems = [];
    listComponent = 'ul';
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imageMatch) {
      flushList();
      const imageUrl = resolveMediaUrl(imageMatch[2]);
      if (!imageUrl) return;

      blocks.push(
        <Box key={`${keyPrefix}-image-${blocks.length}`} component="figure" sx={{ my: 3, mx: 0 }}>
          <Box
            component="img"
            src={imageUrl}
            alt={imageMatch[1]}
            sx={{
              width: '100%',
              maxWidth: 680,
              maxHeight: 420,
              objectFit: 'contain',
              borderRadius: '8px',
              display: 'block',
              mx: 'auto',
              backgroundColor: '#1a1a1a',
            }}
          />
          {imageMatch[1] && (
            <Typography component="figcaption" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
              {imageMatch[1]}
            </Typography>
          )}
        </Box>
      );
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      const level = Math.min(headingMatch[1].length + 1, 4);
      const variant = level === 2 ? 'h5' : level === 3 ? 'h6' : 'subtitle1';
      blocks.push(
        <Typography
          key={`${keyPrefix}-heading-${blocks.length}`}
          component={`h${level}`}
          variant={variant}
          sx={{ mt: 4, mb: 1.5, lineHeight: 1.25 }}
        >
          {renderInlineMarkdown(headingMatch[2])}
        </Typography>
      );
      return;
    }

    const unorderedListMatch = trimmed.match(/^[-*]\s+(.+)$/);
    const orderedListMatch = trimmed.match(/^\d+\.\s+(.+)$/);
    if (unorderedListMatch || orderedListMatch) {
      const nextListComponent = orderedListMatch ? 'ol' : 'ul';
      if (listItems.length > 0 && listComponent !== nextListComponent) flushList();
      listComponent = nextListComponent;
      listItems.push((unorderedListMatch || orderedListMatch)[1]);
      return;
    }

    flushList();
    blocks.push(
      <Typography
        key={`${keyPrefix}-paragraph-${blocks.length}`}
        component="p"
        sx={{ my: 1.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.8 }}
      >
        {renderInlineMarkdown(trimmed)}
      </Typography>
    );
  });

  flushList();
  return blocks;
};

const renderTextNode = (node, key) => {
  let content = node.text || '';

  if (node.code) content = <code>{content}</code>;
  if (node.bold) content = <strong>{content}</strong>;
  if (node.italic) content = <em>{content}</em>;
  if (node.underline) content = <u>{content}</u>;
  if (node.strikethrough) content = <s>{content}</s>;

  if (node.url && isSafeUrl(node.url)) {
    content = (
      <ArticleLink href={node.url}>
        {content}
      </ArticleLink>
    );
  }

  return <React.Fragment key={key}>{content}</React.Fragment>;
};

const renderChildren = (children = []) =>
  children.map((child, index) => {
    if (child.type === 'link' && isSafeUrl(child.url)) {
      return (
        <ArticleLink key={index} href={child.url}>
          {renderChildren(child.children)}
        </ArticleLink>
      );
    }

    return renderTextNode(child, index);
  });

const renderBlock = (block, index) => {
  if (block.__component === 'shared.rich-text' && block.body) {
    return <React.Fragment key={index}>{renderMarkdownBody(block.body, `rich-text-${index}`)}</React.Fragment>;
  }

  switch (block.type) {
    case 'heading': {
      const level = Math.min(Math.max(block.level || 2, 2), 4);
      const variant = level === 2 ? 'h5' : level === 3 ? 'h6' : 'subtitle1';
      return (
        <Typography key={index} component={`h${level}`} variant={variant} sx={{ mt: 4, mb: 1.5, lineHeight: 1.25 }}>
          {renderChildren(block.children)}
        </Typography>
      );
    }
    case 'paragraph':
      return (
        <Typography key={index} component="p" sx={{ my: 1.5, color: 'rgba(255,255,255,0.88)', lineHeight: 1.8 }}>
          {renderChildren(block.children)}
        </Typography>
      );
    case 'quote':
      return (
        <Box
          key={index}
          component="blockquote"
          sx={{
            my: 3,
            mx: 0,
            pl: 2,
            borderLeft: '4px solid #FFD700',
            color: 'rgba(255,255,255,0.78)',
            fontStyle: 'italic',
          }}
        >
          {renderChildren(block.children)}
        </Box>
      );
    case 'list': {
      const Component = block.format === 'ordered' ? 'ol' : 'ul';
      return (
        <Box key={index} component={Component} sx={{ my: 2, pl: 3, lineHeight: 1.8 }}>
          {(block.children || []).map((item, itemIndex) => (
            <li key={itemIndex}>{renderChildren(item.children)}</li>
          ))}
        </Box>
      );
    }
    case 'code':
      return (
        <Box
          key={index}
          component="pre"
          sx={{
            my: 2,
            p: 2,
            overflowX: 'auto',
            borderRadius: '8px',
            backgroundColor: '#171717',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <code>{(block.children || []).map((child) => child.text).join('\n')}</code>
        </Box>
      );
    case 'image': {
      const imageUrl = resolveMediaUrl(block.image?.url);
      if (!imageUrl) return null;

      return (
        <Box key={index} component="figure" sx={{ my: 3, mx: 0 }}>
          <Box
            component="img"
            src={imageUrl}
            alt={block.image?.alternativeText || block.image?.caption || ''}
            sx={{
              width: '100%',
              maxWidth: 680,
              maxHeight: 420,
              objectFit: 'contain',
              borderRadius: '8px',
              display: 'block',
              mx: 'auto',
              backgroundColor: '#1a1a1a',
            }}
          />
          {block.image?.caption && (
            <Typography component="figcaption" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
              {block.image.caption}
            </Typography>
          )}
        </Box>
      );
    }
    default:
      return null;
  }
};

const StrapiBlocksRenderer = ({ blocks }) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return <Typography sx={{ color: 'text.secondary' }}>Brak treści artykułu.</Typography>;
  }

  return (
    <Box
      className="article-content"
      sx={{
        '& a': { color: '#FFD700', textDecoration: 'none' },
        '& a:hover': { textDecoration: 'underline' },
        '& code': {
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '0.92em',
        },
      }}
    >
      {blocks.map(renderBlock)}
    </Box>
  );
};

export default StrapiBlocksRenderer;
